import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Store } from '@ngrx/store';
import { getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { startWith } from 'rxjs';
import { environment } from 'src/environments/environment';

// Declare gtag function for TypeScript
declare function gtag(command: 'config' | 'event' | 'js' | 'set', targetId: string | Date, params?: Record<string, unknown>): void;

export interface GtagEventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: unknown;
}

/**
 * Common event categories for consistent tracking
 * You can use these or pass custom strings
 */
export const GtagCategory = {
  DIALOG: 'dialog',
  MAILTO: 'mailto',
  CALLTO: 'callto',
  GIMMICK: 'gimmick',
} as const;

@Injectable({
  providedIn: 'root',
})
export class GtagService {
  private store = inject(Store);
  private platformId = inject(PLATFORM_ID);
  private cookiePrefs$ = this.store.selectSignal(getCookiePreferences);

  /**
   * Send a custom event to Google Analytics
   */
  sendEvent(eventName: string, params?: GtagEventParams): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!environment.production) {
      console.log('Not production, skipping GA event:', eventName, params);
      return;
    }

    const cookiePrefs = this.cookiePrefs$();
    if (!cookiePrefs?.marketing) {
      return;
    }

    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, params);
    } else {
      console.warn('gtag is not available');
    }
  }

  /**
   * Track button clicks
   */
  trackButtonClick(buttonName: string, category?: string): void {
    this.sendEvent('button_click', {
      event_category: category,
      event_label: buttonName,
    });
  }

  /**
   * Track dialog interactions
   */
  trackDialogOpen(dialogName: string): void {
    this.sendEvent('dialog_open', {
      event_category: GtagCategory.DIALOG,
      event_label: dialogName,
    });
  }

  urlCheck(url: string, context: string): void {
    if (url.startsWith('mailto:') || url.startsWith('mail:')) {
      this.sendEvent('mailto_click', {
        event_category: GtagCategory.MAILTO,
        event_label: context || '',
      });
    }
    if (url.startsWith('tel:') || url.startsWith('callto:')) {
      this.sendEvent('callto_click', {
        event_category: GtagCategory.CALLTO,
        event_label: context,
      });
    }
    // if is external link
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const domain = new URL(url).hostname.replace('www.', '');
      this.sendEvent('external_link_click', {
        event_category: 'navigation',
        event_label: context || '',
        external_domain: domain,
      });
    }
  }
}
